import type {
  ArgsOf,
  RequestOf,
  StagedAccessRequestOf,
} from "../../../../access/types.ts";
import { Access, Delegate } from "../../../../access/types.ts";
import type { PermissionRequest } from "../../../../types/permission.ts";
import { WindowsAccesses } from "../../windows/accesses.ts";

export const AzureRdpAccesses = {
  session: Access({
    description: "RDP session instance",
    label: "RDP Session",
    delegation: {
      "windows-account": Delegate({
        resource: "windows",
        key: "account",
        accesses: WindowsAccesses,
      }),
    },
    generated: {
      principalId: {
        type: "string",
        label: "Principal ID",
        description: "ID of the principal to assign the role to",
      },
      userLoginRoleAssignmentId: {
        type: "string",
        label: "Role Assignment ID",
        description: "Assignment ID of role for virtual machine",
      },
      bastionHostRoleAssignmentId: {
        type: "string",
        label: "Bastion Host Role Assignment ID",
        description: "Assignment ID of role for bastion host",
      },
      networkInterfaceRoleAssignmentId: {
        type: "string",
        label: "Network Interface Role Assignment ID",
        description: "Assignment ID of role for network interface",
      },
      bastionUrl: {
        type: "string",
        label: "Bastion URL",
        description: "URL of the RDP proxy bastion",
      },
      publicKey: {
        type: "string",
        label: "Public Key",
        description: "Public key uploaded for RDP proxy authentication",
        optional: true,
      },
    },
    maturity: "beta",
    parameters: {
      provider: {
        type: "static",
        label: "Provider",
        description: "The RDP provider",
        choices: ["entra", "proxy"] as const,
        optional: true,
      },
      destination: {
        type: "dynamic",
        label: "Destination",
        description: "The destination of the RDP session",
      },
      user: {
        type: "generated",
        label: "User",
        description: "The user for the RDP session",
        optional: true,
      },
      resource: {
        type: "object",
        label: "Data",
        properties: {
          instanceName: {
            type: "generated",
            label: "Instance Name",
          },
          bastionHostId: {
            type: "generated",
            label: "Bastion Host ID",
            description: "ID of the bastion host",
          },
          bastionName: {
            type: "generated",
            label: "Bastion Name",
          },
          bastionRg: {
            type: "generated",
            label: "Bastion Resource Group",
          },
          directoryId: {
            type: "generated",
            label: "Directory ID",
          },
          instanceId: {
            type: "generated",
            label: "Resource ID",
          },
          subscriptionId: {
            type: "generated",
            label: "Subscription ID",
          },
          networkInterface: {
            type: "object",
            label: "Network Interface",
            properties: {
              id: {
                type: "generated",
                label: "Network Interface ID",
              },
              subnetId: {
                type: "generated",
                label: "Subnet ID",
              },
            },
          },
        },
      },
    },
    render: { locator: "destination" } as const,
  }),
};

export type AzureRdpAccesses = typeof AzureRdpAccesses;

export type AzureRdpSessionAccess = AzureRdpAccesses["session"];
export type AzureRdpSessionAccessArgs = ArgsOf<AzureRdpSessionAccess>;
export type AzureRdpSessionRequest = RequestOf<AzureRdpSessionAccess>;

export const isAzureRdpSessionRequest = (
  request: PermissionRequest
): request is StagedAccessRequestOf<typeof AzureRdpAccesses, "session"> & {
  type: "rdp";
} => request.type === "rdp" && request.access === "session";
